import { useState, useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Eye } from 'lucide-react'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import useManagerStore from '../store/managerStore'
import { getTenant, getTenantSpeakbotConfig } from '../api/tenant'
import ChatSettings    from '../components/botconfig/ChatSettings'
import ChatKnowledge   from '../components/botconfig/ChatKnowledge'
import ChatIntegrations from '../components/botconfig/ChatIntegrations'
import ChatStyle       from '../components/botconfig/ChatStyle'
import SpeakSettings   from '../components/botconfig/SpeakSettings'
import SpeakKnowledge  from '../components/botconfig/SpeakKnowledge'

const CHAT_TAB_KEYS  = ['settings', 'knowledge', 'integrations', 'style', 'webWidget']
const SPEAK_TAB_KEYS = ['settings', 'knowledge']

function WebWidgetTab({ tenantId }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [previewHover, setPreviewHover] = useState(false)
  const scriptTag = `<script src="https://api.growchat.ai/static/widget.js" data-tenant="${tenantId}" defer></script>`
  const canPreview = Boolean(tenantId && String(tenantId).trim())

  function openPreview() {
    if (!canPreview) return
    const url = `https://api.growchat.ai/widget/${encodeURIComponent(String(tenantId).trim())}/preview`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  function copy() {
    navigator.clipboard.writeText(scriptTag)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 6, marginTop: 0 }}>{t('botConfig.webWidget.embedTitle')}</p>
          <p style={{ fontSize: 12, color: '#666', margin: 0 }}>{t('botConfig.webWidget.embedHint')}</p>
        </div>
        <button
          type="button"
          onClick={openPreview}
          disabled={!canPreview}
          onMouseEnter={() => setPreviewHover(true)}
          onMouseLeave={() => setPreviewHover(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            alignSelf: 'flex-start',
            background: canPreview && previewHover ? '#f3f4f6' : 'transparent',
            color: '#171717',
            border: 'none',
            borderRadius: 9999,
            padding: '7px 14px',
            fontSize: 12,
            fontWeight: 600,
            cursor: canPreview ? 'pointer' : 'not-allowed',
            opacity: canPreview ? 1 : 0.55,
            transition: 'background 0.15s ease',
          }}
        >
          <Eye size={16} strokeWidth={2} aria-hidden />
          {t('botConfig.webWidget.previewWidget')}
        </button>
      </div>
      <div style={{ background: '#f5f7ff', borderRadius: 8, padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: '#1535c8', wordBreak: 'break-all', border: '0.5px solid #e0e3f0' }}>
        {scriptTag}
      </div>
      <button onClick={copy}
        style={{ marginTop: 14, background: copied ? '#16a34a' : '#1535c8', color: 'white', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}>
        {copied ? t('botConfig.webWidget.copied') : t('botConfig.webWidget.copyCode')}
      </button>
    </div>
  )
}

export default function BotConfigPage() {
  const { t } = useTranslation()
  const { tenantId } = useManagerStore()
  const tid = tenantId

  const [activeBot, setActiveBot] = useState('growchat')
  const [chatTab, setChatTab]     = useState('settings')
  const [speakTab, setSpeakTab]   = useState('settings')
  const [bot, setBot]             = useState(null)
  const [speakConfig, setSpeakConfig] = useState(null)

  const loadData = useCallback(async () => {
    if (!tid) return
    setBot(null)
    try {
      const [tdata, sc] = await Promise.all([getTenant(tid), getTenantSpeakbotConfig(tid)])
      setBot(tdata)
      setSpeakConfig(sc)
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'load_bot_config' },
        extra: { tenantId: tid },
      })
      setBot({})
      setSpeakConfig({})
    }
  }, [tid])

  useEffect(() => {
    if (!tid) {
      const timer = window.setTimeout(() => {
        setBot({})
        setSpeakConfig({})
      }, 0)
      return () => window.clearTimeout(timer)
    }
    const timer = window.setTimeout(() => {
      loadData()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [tid, loadData])

  async function handleChatUpdate() {
    try { setBot(await getTenant(tid)) } catch (err) {
      Sentry.captureException(err, { tags: { action: 'refresh_chat_config' }, extra: { tenantId: tid } })
    }
  }

  async function handleSpeakUpdate() {
    try { setSpeakConfig(await getTenantSpeakbotConfig(tid)) } catch (err) {
      Sentry.captureException(err, { tags: { action: 'refresh_speak_config' }, extra: { tenantId: tid } })
    }
  }

  const accentChat  = '#1535c8'
  const accentSpeak = '#0F6E56'
  const accent  = activeBot === 'growchat' ? accentChat : accentSpeak
  const tabBg   = activeBot === 'growchat' ? '#e8f0fe'  : '#e8f5f0'
  const tabKeys = activeBot === 'growchat' ? CHAT_TAB_KEYS  : SPEAK_TAB_KEYS
  const tab     = activeBot === 'growchat' ? chatTab    : speakTab
  const setTab  = activeBot === 'growchat' ? setChatTab : setSpeakTab

  return (
    <ResponsiveShell title={t('botConfig.title')}>
      <div style={{ paddingBottom: 32 }}>
        {!tid ? (
          <p style={{ fontSize: 14, color: '#666', textAlign: 'center', paddingTop: 48 }}>
            {t('botConfig.needTenant')}
          </p>
        ) : null}

        {tid ? (
        <>
        {/* Bot switcher */}
        <div style={{ display: 'flex', background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 4, marginBottom: 16, gap: 4 }}>
          <button onClick={() => setActiveBot('growchat')}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: activeBot === 'growchat' ? accentChat : 'transparent', color: activeBot === 'growchat' ? 'white' : '#888', transition: 'all 0.15s' }}>
            {t('botConfig.growchat')}
          </button>
          <button onClick={() => setActiveBot('growspeak')}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: activeBot === 'growspeak' ? accentSpeak : 'transparent', color: activeBot === 'growspeak' ? 'white' : '#888', transition: 'all 0.15s' }}>
            {t('botConfig.growspeak')}
          </button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 2 }}>
          {tabKeys.map((tabKey) => (
            <button key={tabKey} onClick={() => setTab(tabKey)}
              style={{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${tab === tabKey ? accent : '#e0e3f0'}`, background: tab === tabKey ? tabBg : 'white', color: tab === tabKey ? accent : '#888', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' }}>
              {t(`botConfig.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        {/* Loading spinner */}
        {bot === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
            <div style={{ width: 32, height: 32, border: `3px solid ${accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {activeBot === 'growchat' && tab === 'settings'     && <ChatSettings     tenantId={tid} bot={bot} onUpdate={handleChatUpdate} />}
            {activeBot === 'growchat' && tab === 'knowledge'    && <ChatKnowledge    tenantId={tid} bot={bot} />}
            {activeBot === 'growchat' && tab === 'integrations' && <ChatIntegrations tenantId={tid} bot={bot} onUpdate={handleChatUpdate} />}
            {activeBot === 'growchat' && tab === 'style'        && <ChatStyle        tenantId={tid} bot={bot} onUpdate={handleChatUpdate} />}
            {activeBot === 'growchat' && tab === 'webWidget'   && <WebWidgetTab     tenantId={tid} />}

            {activeBot === 'growspeak' && tab === 'settings'  && <SpeakSettings  tenantId={tid} speakbotConfig={speakConfig} onUpdate={handleSpeakUpdate} />}
            {activeBot === 'growspeak' && tab === 'knowledge' && <SpeakKnowledge tenantId={tid} speakbotConfig={speakConfig} />}
          </>
        )}
        </>
        ) : null}
      </div>
    </ResponsiveShell>
  )
}
